import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram, Facebook } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";

const NAV = [
  { to: "/shop/mirrors", label: "Mirrors" },
  { to: "/shop/floorboards", label: "Floorboards" },
  { to: "/shop/apparel", label: "Apparel" },
  { to: "/custom", label: "Custom" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#050505] text-[#f3f4f6]">
      {/* Top announcement strip */}
      <div
        className="bg-[#c8102e] text-white text-[11px] tracking-[0.25em] uppercase font-semibold text-center py-2"
        data-testid="announcement-bar"
      >
        Free shipping on orders over $150 · Woman-owned & ride-tested
      </div>

      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10"
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2">
            <span className="font-display text-2xl tracking-wider">
              BAD GIRL <span className="text-[#c8102e]">CUSTOMS</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-sm tracking-[0.18em] uppercase transition-colors ${
                    isActive ? "text-[#c8102e]" : "text-white hover:text-[#c8102e]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="open-cart-button"
              onClick={() => setOpen(true)}
              className="relative p-2 text-white hover:text-[#c8102e] transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-[#c8102e] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 bg-black/95">
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="font-display text-lg tracking-[0.18em] uppercase text-white hover:text-[#c8102e]"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-white/10 mt-24" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-start gap-5">
              <img
                src="/logo.png"
                alt="Bad Girl Customs"
                className="w-24 h-24 object-contain shrink-0 mix-blend-screen opacity-90"
                data-testid="footer-logo-patch"
              />
              <div>
                <h3 className="font-display text-3xl tracking-wider">
                  BAD GIRL <span className="text-[#c8102e]">CUSTOMS</span>
                </h3>
                <p className="text-[#9ca3af] mt-4 max-w-md leading-relaxed">
                  Custom mirrors, hardware, and apparel — designed and built by women who ride. Every piece tested on the road before it ever ships.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <a href="#" data-testid="social-instagram" className="p-2 border border-white/15 hover:border-[#c8102e] hover:text-[#c8102e] transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" data-testid="social-facebook" className="p-2 border border-white/15 hover:border-[#c8102e] hover:text-[#c8102e] transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <div className="bgc-eyebrow mb-4">Shop</div>
            <ul className="space-y-2 text-sm text-[#9ca3af]">
              <li><Link to="/shop/mirrors" className="hover:text-white" data-testid="footer-mirrors">Mirrors</Link></li>
              <li><Link to="/shop/floorboards" className="hover:text-white" data-testid="footer-floorboards">Floorboards</Link></li>
              <li><Link to="/shop/apparel" className="hover:text-white" data-testid="footer-apparel">Apparel</Link></li>
              <li><Link to="/custom" className="hover:text-white" data-testid="footer-custom">Custom Builds</Link></li>
            </ul>
          </div>
          <div>
            <div className="bgc-eyebrow mb-4">Company</div>
            <ul className="space-y-2 text-sm text-[#9ca3af]">
              <li><Link to="/about" className="hover:text-white" data-testid="footer-about">Our Story</Link></li>
              <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/admin/login" className="hover:text-white" data-testid="footer-admin">Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 text-center text-xs text-[#6b7280]">
          © {new Date().getFullYear()} Bad Girl Customs. All rights reserved.
        </div>
      </footer>

      <CartDrawer />
    </div>
  );
}
